import { Pipe, PipeTransform } from '@angular/core';

const ZONA = 'America/Caracas';
const fFecha = new Intl.DateTimeFormat('es-VE', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: ZONA });
const fDia = new Intl.DateTimeFormat('es-VE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fUsd = new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'USD' });

/** "1:00 a. m." para una hora 0-23. */
export function horaTexto(h: number): string {
  const n = ((Math.trunc(h) % 24) + 24) % 24;
  const suf = n < 12 ? 'a. m.' : 'p. m.';
  const h12 = n % 12 === 0 ? 12 : n % 12;
  return `${h12}:00 ${suf}`;
}

export interface OdooEtiqueta { texto: string; clase: string; icono: string; }

const ESTADOS: Record<string, Record<string, OdooEtiqueta>> = {
  maestro: {
    anotado: { texto: 'Anotado en Odoo', clase: 'kb-badge-exito', icono: 'fa-check-circle' },
    pendiente: { texto: 'Pendiente de enviar', clase: 'kb-badge-alerta', icono: 'fa-clock' },
    error: { texto: 'Con problema', clase: 'kb-badge-peligro', icono: 'fa-exclamation-circle' },
  },
  subida: {
    subido: { texto: 'Subido al buzón', clase: 'kb-badge-exito', icono: 'fa-check-circle' },
    anotado: { texto: 'Subido al buzón', clase: 'kb-badge-exito', icono: 'fa-check-circle' },
    con_problemas: { texto: 'Subido al buzón', clase: 'kb-badge-exito', icono: 'fa-check-circle' },
    error: { texto: 'No se pudo subir', clase: 'kb-badge-peligro', icono: 'fa-exclamation-circle' },
  },
  odoo: {
    recibido: { texto: 'Recibido', clase: 'kb-badge-neutro', icono: 'fa-inbox' },
    en_espera: { texto: 'En espera', clase: 'kb-badge-alerta', icono: 'fa-hourglass-half' },
    anotado: { texto: 'Anotado en Odoo', clase: 'kb-badge-exito', icono: 'fa-check-circle' },
    con_problemas: { texto: 'Con filas con problema', clase: 'kb-badge-peligro', icono: 'fa-exclamation-triangle' },
    sin_noticias: { texto: 'Sin noticias todavía', clase: 'kb-badge-neutro', icono: 'fa-minus-circle' },
  },
  conexion: {
    ok: { texto: 'Conectado', clase: 'kb-badge-exito', icono: 'fa-check-circle' },
    sin_configurar: { texto: 'Sin configurar', clase: 'kb-badge-alerta', icono: 'fa-plug' },
    error: { texto: 'Sin conexión', clase: 'kb-badge-peligro', icono: 'fa-exclamation-circle' },
  },
};

/** Estado → insignia con texto (nunca solo color). */
@Pipe({ name: 'odooEstado' })
export class OdooEstadoPipe implements PipeTransform {
  transform(valor: string | null | undefined, grupo: 'maestro' | 'subida' | 'odoo' | 'conexion'): OdooEtiqueta {
    const g = ESTADOS[grupo];
    return (valor && g[valor]) || { texto: valor || '—', clase: 'kb-badge-neutro', icono: 'fa-circle' };
  }
}

/** Fecha y hora en hora de Venezuela. */
@Pipe({ name: 'odooFecha' })
export class OdooFechaPipe implements PipeTransform {
  transform(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : fFecha.format(d);
  }
}

/** Día "YYYY-MM-DD" sin correr de zona. */
@Pipe({ name: 'odooDia' })
export class OdooDiaPipe implements PipeTransform {
  transform(dia: string | null | undefined): string {
    if (!dia) return '—';
    const d = new Date(`${dia.slice(0, 10)}T00:00:00Z`);
    return isNaN(d.getTime()) ? dia : fDia.format(d);
  }
}

@Pipe({ name: 'odooUsd' })
export class OdooUsdPipe implements PipeTransform {
  transform(n: number | null | undefined): string {
    return typeof n === 'number' ? fUsd.format(n) : '—';
  }
}

export const TIPO_TEXTO: Record<string, string> = { cliente: 'Cliente', comercio: 'Comercio' };

@Pipe({ name: 'odooTipo' })
export class OdooTipoPipe implements PipeTransform {
  transform(t: string | null | undefined): string {
    return (t && TIPO_TEXTO[t]) || t || '—';
  }
}

export const ACCION_TEXTO: Record<string, string> = {
  alta: 'Alta', modificacion: 'Modificación', consulta: 'Consulta', token: 'Acceso', archivo: 'Archivo del día',
};
export const ORIGEN_TEXTO: Record<string, string> = {
  aprobacion: 'Al aprobar', 'tiempo-real': 'Al cambiar un dato', manual: 'A mano', cron: 'Automático',
};

@Pipe({ name: 'odooAccion' })
export class OdooAccionPipe implements PipeTransform {
  transform(a: string | null | undefined, que: 'accion' | 'origen' = 'accion'): string {
    const t = que === 'accion' ? ACCION_TEXTO : ORIGEN_TEXTO;
    return (a && t[a]) || a || '—';
  }
}

/** Objeto → texto legible para mostrar "lo que se envió / respondió". */
export function textoLegible(v: unknown): string {
  if (v === null || v === undefined || v === '') return 'Sin datos';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

export function trackPorIndice(i: number): number { return i; }

@Pipe({ name: 'odooHora' })
export class OdooHoraPipe implements PipeTransform {
  transform(h: number | null | undefined): string {
    return typeof h === 'number' ? horaTexto(h) : '—';
  }
}
